import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import connector from '../connector.js';
import actionContext from 'contexts/actions.js';
import ErrorBoundary from 'modules/errors/Boundary.js';
import impl from './@impl/index.jsx';
import './style.css';
import { addFocusOnHover } from '#/lib/add-focus-on-hover.js';

/**
 *
 * @param {internalAPI} API
 * @param {SessionStorage} session
 * @param {ConfigProvider} configProvider
 * @return {{render(*): void}}
 * @constructor
 */
export default function AppConstructor(API, { session, configProvider }) {
  const App = connector(impl);

  return {
    render(container) {
      const root = createRoot(container);

      if (import.meta.env.VITE_TARGET_PLATFORM === 'stv') {
        addFocusOnHover(container);
      }
      if (import.meta.hot) {
        import.meta.hot.on('vite:beforeFullReload', () => {
          root.unmount();
          saveSnapshots();
          reload();

          return new Promise(() => {
            console.log('waiting for full reload');
          });
        });
      }

      container.addEventListener('changeEnv:frontend', (ev) => {
        root.unmount();
        saveSnapshots();
        reload(ev.detail?.target);
      });

      container.addEventListener('changeEnv:backend', (ev) => {
        root.unmount();
        saveSnapshots();
        /**
         * @type {Object}
         * @property {string} api
         * @property {string} broker
         */
        const backend = ev.detail.backend;

        if (backend) {
          session.set('backend', backend);
        } else {
          session.remove('backend');
        }

        reload();
      });

      root.render(
        <React.StrictMode>
          <ErrorBoundary onError={API.panic}>
            <Provider store={API.store}>
              <actionContext.Provider value={API}>
                  <App />
              </actionContext.Provider>
            </Provider>
          </ErrorBoundary>
        </React.StrictMode>
      );
    },
  };

  function saveSnapshots() {
    const config = configProvider.snapshot;
    session.set('config', config);

    const storeSnapshot = structuredClone(API.store.getState());
    storeSnapshot.extensions.forEach((ext) => {
      ext.fetch = 'idle';
      ext.consumable = false;
    });

    session.set('store', storeSnapshot);

    console.log('snapshots saved');
  }

  function reload(target) {
    setTimeout(() => {
      if (!target) {
        // just reload at same target,
        // the possible source webpack fail update module and try to make full page reload;
        window.location.reload();
      } else {
        window.location.href = target;
      }
    }, 42);
  }
}
